import { I18n } from "@assets/strings";
import { List } from "immutable";
import { useObservable } from "micro-observables";
import React, { useMemo, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { GroupDraft } from "@domain/group/groupDraft";
import { Person } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoryEditor } from "@ui/components/category/categoryEditor";
import { Card } from "@ui/components/containers/card";
import { PrimaryBold } from "@ui/components/containers/primary";
import { AssociateOrganizationSelector } from "@ui/components/group/associateOrganizationSelector";
import { ShareroomEditorCancelModal } from "@ui/components/group/shareroom/shareroomEditorCancelModal";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { RoundedTextInput } from "@ui/components/inputs/textInput";
import { row, stack, Stack } from "@ui/components/layout";
import { EditableProfileImage } from "@ui/components/profile/editableProfileImage";
import { UserSearchEngine } from "@ui/components/search/userSearchEngine";
import { SizeCounter } from "@ui/components/sizeCounter";
import { Toolbar } from "@ui/components/toolbar";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { GroupAccessModeEditor } from "./groupAccessModeEditor";
import {
	groupEditionSteps,
	GroupEditorController,
	MAX_SHAREROOM_DESCRIPTION_LENGTH,
	MAX_SHAREROOM_NAME_LENGTH,
} from "@ui/pages/group/groupEditorPage/groupEditorController/groupEditorController";
import { GroupEditorControllerContext } from "@ui/pages/group/groupEditorPage/groupEditorController/groupEditorControllerContext";
import { GroupEditorNavigation } from "./groupEditorNavigation";
import { GroupPrivacyEditor } from "./groupPrivacyEditor";
import { StepIndicator } from "./stepIndicator";

import { MAX_SHAREROOM_MEMBER } from "@domain/group/group";
import { useAdminLimitModal } from "@ui/components/group/shareroom/useAdminLimitModal";

interface GroupEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialGroup: GroupDraft;
	groupId?: string;
	organizationId?: string;
}

export const GroupEditor: React.FC<GroupEditorProps> = ({ initialGroup, groupId, organizationId, ...htmlProps }) => {
	const controller = useMemo(() => new GroupEditorController(initialGroup), []);
	const group = useObservable(controller.group);
	const step = useObservable(controller.step);
	const { type, organization, banner, description, name, categories, rules } = group;
	const [newMembers, setNewMembers] = useState<List<Person>>(List());

	const isMobile = useIsMobile();

	const history = useHistory();

	const isInvalidName = name.length > MAX_SHAREROOM_NAME_LENGTH;
	const isInvalidDescription = description.length > MAX_SHAREROOM_DESCRIPTION_LENGTH;

	const { createModal } = useModalTools();

	const openCancelModal = (shareroom: GroupDraft): void => {
		createModal((close) => <ShareroomEditorCancelModal close={close} shareroom={shareroom} />);
	};

	const openLimitModal = useAdminLimitModal();

	return (
		<GroupEditorControllerContext.Provider value={controller}>
			<Container {...htmlProps}>
				<Left>
					<Title>{groupId ? I18n.t(`group.${type}.edit.title`) : I18n.t(`group.${type}.new.title`)}</Title>
					{!groupId && <StepIndicator steps={groupEditionSteps.length} currentStep={groupEditionSteps.indexOf(step)} />}
					{type === "DISCUSSION_GROUP" ? (
						<PrimaryMessage>
							{step === "INFOS" ? I18n.t(`group.DISCUSSION_GROUP.edit.message`) : I18n.t("group.edit.add_members")}
						</PrimaryMessage>
					) : type === "SHAREROOM" ? (
						<SecondaryMessage>
							{step === "INFOS" ? I18n.t("shareroom.edit.message") : I18n.t("shareroom.edit.add_members")}
						</SecondaryMessage>
					) : null}
					{!isMobile && (
						<AssociateOrganizationSelector
							groupType={type}
							organizationId={organizationId}
							currentOrganization={organization}
							onOrganizationChange={(org) => {
								// console.log("org", org);
								controller.setOrganization(org);
							}}
						/>
					)}
					{!isMobile && (group.type === "SHAREROOM" ? <GroupPrivacyEditor /> : <GroupAccessModeEditor />)}
				</Left>
				{!isMobile && <Divider />}
				<Right>
					{step === "INFOS" ? (
						<>
							{type === "SHAREROOM" ? <BigTitle>{I18n.t("shareroom.edit.info.title")}</BigTitle> : null}
							<EditableProfileImage
								type="banner"
								image={banner}
								label={I18n.t("group.banner.add")}
								sizing={
									type === "SHAREROOM" ? I18n.t("shareroom.edit.banner.sizing", { width: 271, height: 109 }) : undefined
								}
								onUpload={(media) => controller.setBanner(media)}
								onDelete={() => controller.setBanner(undefined)}
								canBeEmpty
							/>
							<Stack gap={20} align="stretch">
								<InfoEdition>
									<PrimaryBold>{I18n.t("group.edition.name")}</PrimaryBold>
									<SimpleTextInput
										value={name}
										onChange={(v) => controller.setName(v)}
										placeholder={I18n.t(`group.${group.type}.name.placeholder`) + "*"}
									/>
									<SizeCounter isInvalid={isInvalidName}>{`${name.length} / ${MAX_SHAREROOM_NAME_LENGTH}`}</SizeCounter>
								</InfoEdition>
								<InfoEdition>
									<PrimaryBold>{I18n.t("group.edition.description")}</PrimaryBold>
									<DescriptionInput
										value={description}
										onChange={(e) => controller.setDescription(e.currentTarget.value)}
										placeholder={I18n.t(`group.${type}.description.placeholder`) + "*"}
									/>
									<SizeCounter
										isInvalid={isInvalidDescription}
									>{`${description.length} / ${MAX_SHAREROOM_DESCRIPTION_LENGTH}
									`}</SizeCounter>
								</InfoEdition>

								{/* {!groupId && ( */}
								<InfoEdition>
									<PrimaryBold>{I18n.t("group.edition.category")}</PrimaryBold>
									<CategoryEditor
										category={categories[0]}
										setCategory={(c) => controller.setMainCategory(c)}
										label={I18n.t(`category.placeholder.${type}`)}
									/>
								</InfoEdition>
								{/* )} */}
								{group.type === "DISCUSSION_GROUP" && (
									<InfoEdition>
										<PrimaryBold>{I18n.t("group.edition.rules")}</PrimaryBold>
										<RulesInput
											placeholder={I18n.t(`group.${group.type}.rules.placeholder`)}
											value={rules}
											onChange={(v) => controller.setRules(v)}
										/>
									</InfoEdition>
								)}
								{isMobile && (
									<>
										<AssociateOrganizationSelector
											groupType={type}
											organizationId={organizationId}
											currentOrganization={organization}
											onOrganizationChange={(org) => controller.setOrganization(org)}
										/>
										{group.type === "SHAREROOM" ? <GroupPrivacyEditor /> : <GroupAccessModeEditor />}
									</>
								)}
							</Stack>
						</>
					) : step === "MEMBERS" ? (
						<>
							{type === "SHAREROOM" ? <BigTitle>{I18n.t("shareroom.edit.add_member.title")}</BigTitle> : null}
							{!groupId && (
								<UserSearchEngine
									newUsers={newMembers}
									onNewUsersChange={(users, action) => {
										if (type === "SHAREROOM" && newMembers.size >= MAX_SHAREROOM_MEMBER && action === "ADD") {
											openLimitModal();
											return;
										}
										setNewMembers(users);
									}}
									resultsMaxHeight={240}
									selectionPosition="bottom"
									showOnlySearchResults={isMobile}
								/>
							)}
						</>
					) : null}
				</Right>
				<QuitEditorButton onClick={() => (type === "SHAREROOM" ? openCancelModal(group) : history.push(Path.Root))}>
					{I18n.t(groupId ? "group.edit.quit" : "group.creation.quit")}
				</QuitEditorButton>
			</Container>
			{isMobile ? (
				<>
					<MobileNavigation groupId={groupId} newMemberIds={newMembers.map((member) => member.id).toArray()} />
				</>
			) : (
				<StyledToolbar>
					<GroupEditorNavigation groupId={groupId} newMemberIds={newMembers.map((member) => member.id).toArray()} />
				</StyledToolbar>
			)}
		</GroupEditorControllerContext.Provider>
	);
};

const Container = styled(Card)`
	position: relative;
	padding: 77px 0px 58px;
	@media ${desktop} {
		${row(0, "space-between", "stretch")};
		margin: auto;
		max-width: 1130px;
	}

	@media ${mobile} {
		padding: 40px 20px 80px;
		${stack(0, "flex-start", "stretch")};
	}
`;

const Left = styled.div`
	@media ${desktop} {
		width: 50%;
		padding: 0 75px;
	}
	${gap(40)};
`;
const Right = styled.div`
	@media ${desktop} {
		width: 50%;
		padding: 0 75px;
	}
	${gap(54)};
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
	margin-bottom: 8px;
`;

const PrimaryMessage = styled.div`
	${({ theme }) => theme.textStyles.primary};
	${({ theme }) => theme.textStyles.secondaryFontFamily};
	padding-bottom: 30px;
`;
const SecondaryMessage = styled.div`
	${({ theme }) => theme.textStyles.simpleDescription};
	padding-bottom: 30px;
`;

const Divider = styled.div`
	width: 1px;
	height: 100%;
	min-height: 450px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;

const QuitEditorButton = styled(TertiaryButton)`
	text-decoration: none;
	${({ theme }) => theme.textStyles.label};
	position: absolute;
	bottom: 30px;
	@media ${desktop} {
		right: 30px;
	}
	@media ${mobile} {
		left: 20px;
	}
`;

const StyledToolbar = styled(Toolbar)`
	> * {
		flex: 1;
		max-width: 630px;
	}
`;

const MobileNavigation = styled(GroupEditorNavigation)`
	margin-top: 40px;
`;

const RulesInput = styled(RichTextInput)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	border-color: ${({ theme }) => theme.colors.lightergray};
	border-radius: 7px;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 20px;
		font-size: 0.8125rem;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const DescriptionInput = styled(RoundedTextInput)`
	border-radius: 7px;
	transition: border-color 0.2s linear;

	:focus {
		border-color: #6f6f6f;
	}

	height: 50px !important;
	padding: 15px;
`;

const BigTitle = styled.div`
	@media ${mobile} {
		display: none;
	}
	${({ theme }) => theme.textStyles.title};
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.25rem;
`;
